// @ts-nocheck

import dom from "./dom2.js";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const dontTreeShakeDom = dom;
import tippy, {createSingletonWrapper} from "./common/tippy-control.js";
import ShadowHost from "./common/components/ShadowElements.jsx";
import Project from "../../main/project.js";
import ProjectTopicMenuPanel, {AddTopicButton} from "./components/project-topic-menu/ProjectTopicMenuPanel.jsx";
import TopicList from "./components/project-topic-menu/TopicList.jsx";
import TopicCreate from "./components/project-topic-menu/TopicCreate.jsx";
import Data from "../../main/data/index.js";
import type {annotationUuid, projectUuid, topic, project} from "../../main/data/model/index.js";
import {waitUntil} from "../../main/utils/async.js";
import {DEFAULT, TOPIC_COLORS} from "../../main/constants/colors.js";
import Integration from "../integration/index.js";

export const PROJECT_TOPIC_MENU_JS_SELECTOR = ".js-pn-project-topic-menu";

const findFirstTopicUuid = "__FIRST__";
const findSecondTopicUuid = "__SECOND__";

const defaultTopicsForIntegration = [
	{
		uuid:     findFirstTopicUuid,
		color:    TOPIC_COLORS[DEFAULT].topics[0],
		label:    "Background",
		position: 1
	},
	{
		uuid:     findSecondTopicUuid,
		color:    TOPIC_COLORS[DEFAULT].topics[1],
		label:    "New Topic",
		position: 2
	}
];

export const getProjectTopicMenu = function () {
	const container = document.querySelector(PROJECT_TOPIC_MENU_JS_SELECTOR);
	if (container)
		return container.shadowRoot.getElementById("project-topic-menu");
	else
		return null;
};

class ProjectTopicMenu {
	beforeFirstDataInteraction: Promise<void> = () => Promise.resolve();

	//
	// PUBLIC FUNCTIONS
	//

	constructor(params) {
		// Img src
		this.imgSrc = "";

		// params keys: {data, hooks}
		/** @type {App} */
		this.context = params.data.context; // sloppy integration until refactor is complete
		/** @type {WebsiteBehaviors} */
		this.websiteBehaviors = params.data.websiteBehaviors;

		this.copySelectedText = params.hooks.copySelectedText;

		if (params.hooks.beforeFirstDataInteraction)
			this.beforeFirstDataInteraction = params.hooks.beforeFirstDataInteraction;

		this.resetAppendedSpacesTimeout = null;

		this.isExistingHighlight = false; // whether the associated highlight is already saved

		this.tippyInstances = [];
		this.tippySingleton = null;
	}

	/**
	 * Render topic menu at specified position.
	 * If an annotation uuid is passed, it is used to determine the initially selected topic.
	 *
	 * @param {number} top
	 * @param {number} left
	 * @param {string|undefined} annotationUuid Annotation UUID (or `undefined` if not relevant)
	 * @param {boolean} isKeyboard User used keyboard to open the topic menu
	 * @param {function} onTopicSelect Callback which receives the new topic uuid
	 */
	renderMenuAt(top, left, annotationUuid, isKeyboard, onTopicSelect) {
		this.isExistingHighlight = annotationUuid !== undefined;
		// TODO: Can we rework this so we're not passing in the annotation uuid?

		// onTopicSelect is only passed when called from outside this file.
		if (onTopicSelect !== undefined)
			this.onTopicSelect = onTopicSelect;

		this.renderMenu(top, left, annotationUuid, () => {
			if (isKeyboard) {
				const projectTopicMenuRoot = document.querySelector(PROJECT_TOPIC_MENU_JS_SELECTOR).shadowRoot;

				const firstTopicBtn = projectTopicMenuRoot.querySelector(".js-btn-topic");
				if (firstTopicBtn) // Focus first topic button
					firstTopicBtn.focus();
				else // Focus first action button at the bottom
					projectTopicMenuRoot.querySelector(".js-actions button").focus();
			}
		});
	}

	/**
	 * Destroys the highlight menu and its event listeners.
	 */
	destroy() {
		if (this.isMenuOpen())
			this.removeMenu();

		this.context.renderer.removeTmpAnnotation();
		this.resetAppendedSpacesTimeout = setTimeout(this.context.renderer.resetAppendedSpaces.bind(this.context.renderer), 500);
	}

	/**
	 * Returns whether the highlight menu is open and visible.
	 *
	 * @return  bool
	 */
	isMenuOpen() {
		return !!(document.querySelector(PROJECT_TOPIC_MENU_JS_SELECTOR));
	}

	/**
	 * Refreshes the topic list on the topics menu.
	 */
	refreshTopicsOnMenu() {
		const menu = document.querySelector(PROJECT_TOPIC_MENU_JS_SELECTOR);
		if (!menu)
			return;

		const annotationUuid = menu.getAttribute("data-annotation-uuid") ?? null;
		const topicUuid = Data.outline.getTopicUuidForAnnotation(annotationUuid, Data.app.project.uuid);

		const topics = Data.app.topics;
		const newTopicList = dom(TopicList, {
			topics,
			onClickTopic:      this.handleClickOnTopicBtn.bind(this),
			selectedTopicUuid: topicUuid
		});
		const topicList = menu.shadowRoot.querySelector(".js-topics");
		if (topicList)
			topicList.replaceWith(newTopicList);

		// Replace add topic button in case topics can or can no longer be added
		const newAddButton = dom(AddTopicButton, {
			canAddTopics:    Project.canAddNewTopics(Data.app.project, topics.length),
			onClickAddTopic: this.handleClickOnShowAddTopicBtn.bind(this)
		});
		const addButton = menu.shadowRoot.querySelector(".js-btn-show-add-topic");
		if (addButton)
			addButton.replaceWith(newAddButton);
		else if (newAddButton) { // button doesn't exist currently, add it
			const actionButtons = menu.shadowRoot.querySelector(".js-actions");
			if (!actionButtons) {
				// Do nothing - the topic menu we were working on most likely
				// got replaced by a new one due to a sync event
				return;
			} else if (actionButtons.querySelector(".js-btn-edit-topics"))
				actionButtons.insertBefore(newAddButton, actionButtons.lastElementChild);
			else
				actionButtons.append(newAddButton);
		}

		// Redo tooltips
		this.tippyInstances.forEach(inst => inst.destroy());
		this.tippyInstances = tippy(menu.shadowRoot.querySelectorAll(".js-actions [data-tippy-content]"));
		this.tippySingleton.setInstances(this.tippyInstances);
	}

	/**
	 * Re-renders topic popup in the same location.
	 */
	reRenderTopicsPopup() {
		const menu = document.querySelector(PROJECT_TOPIC_MENU_JS_SELECTOR);
		if (!menu)
			return;

		const shadowMenu = getProjectTopicMenu();
		const top = parseInt(shadowMenu.style.top, 10);
		const left = parseInt(shadowMenu.style.left, 10);
		const annotationUuid = menu.getAttribute("data-annotation-uuid") ?? null;

		this.removeMenu();
		this.renderMenu(top, left, annotationUuid);

		this.scrollToBottom();

		// WARNING: Hacky fix to prevent sync events from undoing our scrollToBottom().
		setTimeout(this.scrollToBottom, 1000);
	}

	//
	// PRIVATE FUNCTIONS
	//

	/**
	 * Hide highlight menu and removes it from the DOM.
	 */
	removeMenu() {
		document.querySelector(PROJECT_TOPIC_MENU_JS_SELECTOR).remove();
	}

	/**
	 * Render highlight menu.
	 */
	renderMenu(top: number, left: number, uuid: annotationUuid = undefined, onRender: () => void = undefined) {
		const project: project | undefined = Data.app.project;

		if (uuid && !project) {
			console.error("PN: Tried to render highlight menu when project is undefined.");
			return;
		}

		// Let's normalize the values of top and left, shall we
		top = parseInt(top.toString().replace("px", ""), 10);
		left = parseInt(left.toString().replace("px", ""), 10);

		// Now please continue with your day
		const topicUuid = uuid
			? Data.outline.getTopicUuidForAnnotation(uuid, project.uuid)
			: undefined;

		// If we were going to reset "appended spaces" on DIVs, cancel that until we close this menu
		if (this.resetAppendedSpacesTimeout) {
			clearTimeout(this.resetAppendedSpacesTimeout);
			this.resetAppendedSpacesTimeout = null;
		}

		// Remove any menu that might be open.
		if (this.isMenuOpen())
			this.removeMenu();
		this.context.commentMenu.destroy();

		let selectedProjectUuid: projectUuid | null;
		let topics: topic[];
		let projects: project[] | null;
		let projectTopicsUrl: string | null;

		if (project) {
			selectedProjectUuid = project.uuid;
			topics = Data.app.topics;
			projects = Data.app.acceptableProjects;
			projectTopicsUrl = `${Data.app.config?.urls.pn_www}project/${project.uuid}/outline#/topics`;
		} else {
			selectedProjectUuid = null;
			topics = defaultTopicsForIntegration;
			projects = null;
			projectTopicsUrl = null;
		}

		// Construct topic menu panel
		const projectTopicMenuPanel = dom(ProjectTopicMenuPanel, {
			left,
			projects,
			selectedProjectUuid,
			top,
			topics,
			annotationUuid:            uuid,
			calculateListHeights:      this.calculateListHeights,
			canAddTopics:              !project || Project.canAddNewTopics(project, topics.length),
			canEditTopics:             !Integration.isIntegration() && Project.hasTopicsEditingAccess(project),
			copyButtonLabel:           this.websiteBehaviors.getCopyButtonLabel(),
			onClickCopy:               this.handleCopyHighlight.bind(this),
			onClickAddTopic:           this.handleClickOnShowAddTopicBtn.bind(this),
			onClickDeleteAnnotation:   async () => {await this.handleDeleteAnnotation(uuid);},
			onClickProject:            this.handleClickOnProjectBtn.bind(this),
			onClickSwitchBtn:          this.handleSwitchMode.bind(this),
			onClickTopic:              this.handleClickOnTopicBtn.bind(this),
			onClickHelp:               this.handleClickOnHelpBtn.bind(this),
			onClickDisable:            this.handleClickOnDisableBtn.bind(this),
			onKeyDownProjectTopicMenu: this.handleKeyDownOnProjectTopicMenu.bind(this),
			project:                   project,
			projectTopicsUrl:          projectTopicsUrl,
			selectedTopicUuid:         topicUuid
		});

		// Set up topic menu shadow host
		const projectTopicMenuShadowHost = dom(ShadowHost, {
			classId:        PROJECT_TOPIC_MENU_JS_SELECTOR.substr(1),
			onStylesLoaded: (shadow) => {
				this.addToDOM(shadow, projectTopicMenuPanel, top, left);

				if (onRender)
					onRender();
			},
			stylesheet: "project-topic-menu.css",
			...(uuid && {"data-annotation-uuid": uuid})
		});
		// Add to DOM so styles can start loading
		document.body.append(projectTopicMenuShadowHost);

		// Add tooltips. Use a singleton so that they'll never overlap each other
		this.tippyInstances = tippy(projectTopicMenuPanel.querySelectorAll(".js-actions [data-tippy-content]"));
		if (this.tippySingleton)
			this.tippySingleton.setInstances(this.tippyInstances);
		else {
			this.tippySingleton = createSingletonWrapper(
				this.tippyInstances,
				{placement: "bottom"}
			);
		}
	}

	addToDOM(shadow, projectTopicMenuPanel, top, left) {
		shadow.append(projectTopicMenuPanel);

		// Fix menu position if outside of viewport.
		const newMenuPosition = this.getFixedMenuPosition(projectTopicMenuPanel);

		if (newMenuPosition.top < 0)
			projectTopicMenuPanel.style.top = (top + newMenuPosition.top) + "px";

		if (newMenuPosition.left < 0)
			projectTopicMenuPanel.style.left = (left + newMenuPosition.left) + "px";

		projectTopicMenuPanel.style.visibility = "visible";
	}

	/**
	 * Returns an object with top and left values for the menu.
	 * If negative, they represent correction values to apply to bring the menu into the viewport.
	 *
	 * @param   {Object}  menu  menu dom element
	 * @return  {Object}
	 */
	getFixedMenuPosition(menu) {
		const rect = menu.getBoundingClientRect();

		// jquery's height() and width() functions resolve down to
		// window.document.documentElement["clientHeight"] and
		// window.document.documentElement["clientWidth"]
		// for windows. So we'll just use them here directly.

		let clientHeight = window.document.documentElement["clientHeight"];
		let clientWidth = window.document.documentElement["clientWidth"];

		// Firefox returns 0 "for elements with no CSS or inline layout boxes"
		// (https://developer.mozilla.org/en-US/docs/Web/API/Element/clientHeight)
		// but chrome seems to return the innerHeight in that case.
		if (clientHeight === 0)
			clientHeight = window.innerHeight;

		// We'll assume the same can happen with the width.
		if (clientWidth === 0)
			clientWidth = window.innerWidth;

		// TODO: Why do we subtract 30 here? Is this just arbitrary padding?
		const windowHeight = clientHeight - 30;
		const windowWidth = clientWidth - 30;

		return {
			top:  windowHeight - rect.bottom,
			left: windowWidth - rect.right
		};
	}

	handleBlurOnTopicLabelOnMenu(evt) {
		this.context.renderer.removeTmpAnnotation.bind(this.context.renderer)();
	}

	/**
	 * Handles click on topic buttons inside the highlight menu.
	 *
	 * @param  {string} topicUuid
	 */
	async handleClickOnTopicBtn(topicUuid) {
		// TODO: Can we make this cleaner in the case of screenshots?

		await this.beforeFirstDataInteraction();

		this.context.renderer.removeTmpAnnotation.bind(this.context.renderer)();
		this.context.renderer.recoverLostSelection.bind(this.context.renderer)();

		const menu = getProjectTopicMenu();
		const menuTop = menu.style.top.replace("px", "");
		const menuLeft = menu.style.left.replace("px", "");

		switch (topicUuid) {
			case findFirstTopicUuid:
				await waitUntil(() => Data.app.topics.length > 0);
				topicUuid = Data.app.topics[0].uuid;
				break;
			case findSecondTopicUuid:
				await waitUntil(() => Data.app.topics.length > 0);
				if (Data.app.topics.length >= 2)
					topicUuid = Data.app.topics[1].uuid;
				else
					topicUuid = Data.app.topics[0].uuid;
				break;
		}

		this.onTopicSelect(topicUuid, menuTop, menuLeft);
		this.destroy();
	}

	/**
	 * Handles click on help button inside the highlight menu.
	 */
	async handleClickOnHelpBtn() {
		const myEvent = new CustomEvent("pnShowHelpProject", {
			bubbles:  true,
			composed: true
		});

		dispatchEvent(myEvent);
	}

	/**
	 * Handles click on disable button inside the highlight menu.
	 */
	async handleClickOnDisableBtn() {
		const myEvent = new CustomEvent("pnDisable", {
			bubbles:  true,
			composed: true
		});

		dispatchEvent(myEvent);

		Integration.gtag("event", "disable");
	}

	/**
	 * Handles keydown anywhere in the menu. Closes the menu on [esc]
	 */
	handleKeyDownOnProjectTopicMenu(event: KeyboardEvent) {
		const key = event.key;

		if (key === "Escape" || key === "Esc")
			this.destroy();
			// TODO: return keyboard focus to last focused non-PN element
	}

	/** Handles click on save button on topic creation box. **/
	async handleClickOnSaveTopic() {
		await this.beforeFirstDataInteraction();

		const exitWithError = function (errorMessage = "") {
			// Recover selection that was lost due to focus on input.
			this.context.renderer.removeTmpAnnotation.bind(this.context.renderer)();
			this.context.renderer.recoverLostSelection.bind(this.context.renderer)();
			this.context.renderer.saveSelectionTemporarily.bind(this.context.renderer)();

			if (errorMessage !== "")
				console.error(errorMessage); // TODO

		}.bind(this);
		const exitWithSuccess = function () {
			// Recover selection that was lost due to focus on input.
			this.context.renderer.removeTmpAnnotation.bind(this.context.renderer)();
			this.context.renderer.recoverLostSelection.bind(this.context.renderer)();
		}.bind(this);

		const menu = document.querySelector(PROJECT_TOPIC_MENU_JS_SELECTOR);
		if (!menu)
			return exitWithError();

		const topicCreate = menu.shadowRoot.querySelector(".js-topic-create");
		if (!topicCreate)
			return exitWithError();

		const label = topicCreate.querySelector('input[name="label"]');
		const labelValue = label.value.trim();
		const color = topicCreate.querySelector('input[name="color"]').value;

		label.classList.remove("error");

		// Check if label is empty.
		if (!labelValue.length) {
			label.classList.add("error");
			return exitWithError();
		}

		const normalize = label => label.toLowerCase().trim();
		const isDuplicated = !!Data.app.topics.find(t => normalize(t.label) === normalize(labelValue));
		if (isDuplicated) {
			label.classList.add("error");
			return exitWithError("Duplicate topic name.");
		}

		topicCreate.remove();

		try {
			await waitUntil(() => Data.app.project !== undefined);

			await Data.topic.create({
				project_uuid: Data.app.project.uuid,
				label:        labelValue,
				color:        color
			});

			this.refreshTopicsOnMenu();

			this.scrollToBottom();
		} catch (e) {
			console.error(e);
		}

		return exitWithSuccess();
	}

	/**
	 * Scrolls to the bottom of the topics list
	 */
	scrollToBottom = () => {
		const menu = document.querySelector(PROJECT_TOPIC_MENU_JS_SELECTOR);
		if (!menu)
			return;

		const topicsList = menu.shadowRoot.querySelector("ul.js-topics");
		if (topicsList) {
			topicsList.scrollTo({
				top:      topicsList.scrollHeight,
				behavior: "smooth"
			});
		}
	};

	/**
	 * Calculates and sets the height for both the topics and projects lists in
	 * px. Heights are calculated based on having the max height of the entire
	 * menu to be 32vh. Changing the heights will automatically cause transitions
	 * to happen.
	 *
	 * @param {menuContainer} HTMLElement
	 **/
	calculateListHeights = (menuContainer) => {
		const px_32vh = Math.round(window.innerHeight * 32 / 100); // 32vh in pixels

		const listItemHeight = Integration.name === "proquest" ? 37.5 : 33;

		// Is it showing projects or topics list?
		if (menuContainer.classList.contains("mode-project-selection")) {
			// Showing project list. Calculate project list height by subtracting
			// the height of the "Select a project" label
			const maxHeightAllowed = px_32vh - 32; // calc(32vh - 32px)
			const numProjects = menuContainer.querySelectorAll(".project-item").length;
			const pListTotalHeight = numProjects * listItemHeight; // height of entire projects list in px

			// Limit the height to maxHeightAllowed
			const height = Math.min(maxHeightAllowed, pListTotalHeight);
			menuContainer.style.setProperty("--project-list-height", height + "px");

			// Set topic list height to 0px to close it
			menuContainer.style.setProperty("--topic-list-height", "0px");
		} else {
			// Showing topic list. Calculate topic list height by subtracting the
			// height of the selected project label and the action buttons
			const maxHeightAllowed = px_32vh - 32 - 35; // calc(32vh - 32px - 35px)
			const numTopics = menuContainer.querySelectorAll(".topic-item, .js-topic-create").length;
			const tListTotalHeight = numTopics * listItemHeight; // height of entire topics list in px

			// Limit the height to maxHeightAllowed
			const height = Math.min(maxHeightAllowed, tListTotalHeight);
			menuContainer.style.setProperty("--topic-list-height", height + "px");

			// Set topic list height to 32px to show the selected project
			menuContainer.style.setProperty("--project-list-height", "32px");
		}
	};

	handleClickOnShowAddTopicBtn() {
		this.showTopicCreation();
		this.calculateListHeights(getProjectTopicMenu());
	}

	/**
	 * Switches between topic or project selection mode and recalculates the
	 * height of each list. Changing mode selection classes will automatically
	 * cause CSS transitions to happen.
	 **/
	handleSwitchMode() {
		const menuContainer = getProjectTopicMenu();

		menuContainer.classList.toggle("mode-topic-selection");
		menuContainer.classList.toggle("mode-project-selection");

		this.calculateListHeights(menuContainer);

		// Reset scroll of projects list so the topic name shows in the collapsed form.
		// (Otherwise it won't show if the user scrolled down.)
		menuContainer.querySelector(".js-projects").scrollTop = 0;

		this.context.renderer.recoverLostSelection.bind(this.context.renderer)();
	}

	/**
	 * Handles focus on topic label input on topics popup.
	 * Creates a temp annotation to simulate the native selected text.
	 *
	 * @param  object  evt
	 */
	handleFocusOnTopicLabelOnMenu(evt) {
		if (!this.isExistingHighlight)
			this.context.renderer.renderTempHighlight.bind(this.context.renderer)(this.websiteBehaviors, Data.app.me);
	}

	/**
	 * Handles keypress on topic label input on topics popup.
	 *
	 * @param  object  evt
	 */
	handleKeyupOnTopicLabelOnMenu(evt) {
		if (evt.key === "Enter")
			this.handleClickOnSaveTopic();
	}

	/**
	 * Copy the selected text to the user's clipboard.
	 * This can only happen when the user has selected text but not picked a topic
	 * (as opposed to when we use the topic menu to reassign the topic of an existing highlight).
	 */
	async handleCopyHighlight() {
		await this.copySelectedText();
		this.destroy();
	}

	/**
	 * Event handler for user request to delete an annotation.
	 * @see sister function in comment-menu.js
	 *
	 * @param {string} annotationUuid
	 * @return {Promise}
	 */
	async handleDeleteAnnotation(annotationUuid) {
		if (!Data.app.project)
			throw new Error();
		this.destroy();
		await Data.annotation.delete(annotationUuid, Data.app.project.uuid);
	}

	/**
	 * Shows topic creation on topics popup menu.
	 */
	showTopicCreation() {
		const menu = document.querySelector(PROJECT_TOPIC_MENU_JS_SELECTOR);
		if (!menu)
			return;
		let topicCreate = menu.shadowRoot.querySelector(".js-topic-create");
		if (topicCreate)
			return;

		// Save current text selection, in order to recover it.
		this.context.renderer.saveSelectionTemporarily.bind(this.context.renderer)();

		topicCreate = dom(TopicCreate, {
			onClickSaveTopic:    this.handleClickOnSaveTopic.bind(this),
			onFocusOnTopicLabel: this.handleFocusOnTopicLabelOnMenu.bind(this),
			onBlurOnTopicLabel:  this.handleBlurOnTopicLabelOnMenu.bind(this),
			onKeyupOnTopicLabel: this.handleKeyupOnTopicLabelOnMenu.bind(this),
			topics:              Data.app.project ? Data.app.topics : defaultTopicsForIntegration
		});

		menu.shadowRoot.querySelector(".js-topics").append(topicCreate);

		topicCreate.querySelector(".js-new-topic-name").focus();
	}

	/**
	 * Selects the project if the project chosen is not the currently selected
	 * project, then switches to topic selection mode. In main.js changeProjectTo(),
	 * the topic menu is prevented from being destroyed if the projects selected
	 * are differing. This is so that the topic menu will not be re-rendered,
	 * allowing transitions to be seen.
	 */
	async handleClickOnProjectBtn(projectUuid: projectUuid): Promise<void> {
		if (projectUuid === Data.app.project.uuid)
			this.handleSwitchMode();
		else {
			this.context.renderer.saveSelectionTemporarily.bind(this.context.renderer)();

			// Start loading animation
			const menu = getProjectTopicMenu();
			const loadingBox = menu.querySelector(".aa-loading-box");
			loadingBox.classList.add("show");

			// Select project (changes sidebar too)
			Data.app.setCurrentProject(projectUuid);
			Data.app.topics; // trigger loading topics from backend (ugly, yes, but we're not "reactive" around this piece of code yet)
			if (!Data.topic._isProjectLoaded(projectUuid))
				await waitUntil(() => Data.topic._isProjectLoaded(projectUuid)); // also ugly

			// Update topic list
			const newTopicList = dom(TopicList, {
				onClickTopic: this.handleClickOnTopicBtn.bind(this),
				topics:       Data.app.topics
			});

			const topicList = menu.querySelector(".js-topics");
			if (topicList)
				topicList.replaceWith(newTopicList);

			// Update selected project in UI
			menu.querySelector(".project-item.selected").classList.remove("selected");
			menu.querySelector(`.js-btn-project[data-uuid="${projectUuid}"]`).closest(".project-item").classList.add("selected");

			try {
				// Recover text selection, lost when the project button was clicked
				this.context.renderer.recoverLostSelection.bind(this.context.renderer)();

				// Stop loading animation
				loadingBox.classList.remove("show");

				// Switch back to topic selection mode
				this.handleSwitchMode();
			} catch (e) {
				// It's likely that the new highlight-in-progress overlapped with
				// a highlight from the project we switched to, so let's just
				// get rid of the higlight menu and let the user try again.
				this.destroy();
				// Also clear the lost selection, or we'll have trouble creating more highlights
				this.context.renderer.clearLostSelection.bind(this.context.renderer)();
			} finally {
				this.context.isProjectsSwitchFromMenu = false;
			}
		}
	}
}

export default ProjectTopicMenu;
